import { useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";

/**
 * Public marketing landing page at "/".
 *
 * Authenticated users are redirected to /dashboard automatically.
 * Unauthenticated users see the value proposition and CTA to /login.
 *
 * Design principles: clean, premium, trustworthy. No animation gimmicks.
 * Mobile-first responsive via Tailwind.
 */
export function LandingPage() {
  const { isAuthenticated, isLoading } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    document.title = "GetCitizen AI — Prepare Your N-400 Application in 45 Minutes";
  }, []);

  // Authenticated users don't need to see the landing page.
  useEffect(() => {
    if (!isLoading && isAuthenticated) {
      navigate("/dashboard", { replace: true });
    }
  }, [isAuthenticated, isLoading, navigate]);

  // Don't flash the landing page while auth state resolves.
  if (isLoading) return null;
  if (isAuthenticated) return null;

  return (
    <div className="min-h-screen flex flex-col bg-white text-slate-900">
      {/* ── Nav ─────────────────────────────────────────────── */}
      <header className="sticky top-0 z-10 bg-white/90 backdrop-blur-sm border-b border-slate-100">
        <div className="max-w-5xl mx-auto px-6 h-14 flex items-center justify-between">
          <Logo />
          <Link
            to="/login"
            className="text-sm font-semibold text-slate-600 hover:text-blue-600 transition-colors"
          >
            Sign in
          </Link>
        </div>
      </header>

      <main className="flex-1">
        {/* ── Hero ────────────────────────────────────────────── */}
        <section className="max-w-3xl mx-auto px-6 pt-20 pb-16 text-center">
          {/* Badge */}
          <div className="inline-flex items-center gap-2 bg-blue-50 text-blue-700 text-xs font-semibold px-3 py-1 rounded-full border border-blue-100 mb-6">
            <span className="w-1.5 h-1.5 rounded-full bg-blue-500 shrink-0" />
            N-400 Naturalization · January 2025 edition
          </div>

          <h1 className="text-4xl sm:text-5xl font-bold tracking-tight text-slate-900 leading-tight">
            Prepare your N-400 citizenship<br className="hidden sm:block" /> application in 45 minutes
          </h1>

          <p className="mt-5 text-lg text-slate-500 leading-relaxed max-w-xl mx-auto">
            Answer guided questions in plain English. AI explains every field.
            Download a fully pre-filled PDF draft ready to review and file.
          </p>

          {/* Badge row */}
          <div className="mt-5 flex flex-wrap items-center justify-center gap-x-4 gap-y-2 text-sm text-slate-500 font-medium">
            <span className="flex items-center gap-1.5">
              <span className="text-blue-500">✓</span> $49 to download
            </span>
            <span className="text-slate-200 hidden sm:inline">|</span>
            <span className="flex items-center gap-1.5">
              <span className="text-blue-500">✓</span> Free to start
            </span>
            <span className="text-slate-200 hidden sm:inline">|</span>
            <span className="flex items-center gap-1.5">
              <span className="text-blue-500">✓</span> Not a law firm
            </span>
          </div>

          {/* CTAs */}
          <div className="mt-8 flex flex-col sm:flex-row items-center justify-center gap-3">
            <Link
              to="/login"
              className="w-full sm:w-auto inline-flex items-center justify-center gap-2 rounded-full bg-blue-600 text-white px-7 py-3.5 text-sm font-semibold hover:bg-blue-700 hover:shadow-lg hover:-translate-y-0.5 transition-all duration-200 shadow-md"
            >
              Start your N-400 draft
              <svg className="w-4 h-4" fill="none" viewBox="0 0 16 16" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M3 8h10M9 4l4 4-4 4" />
              </svg>
            </Link>
            <a
              href="#how-it-works"
              className="w-full sm:w-auto inline-flex items-center justify-center gap-2 rounded-full border border-slate-200 text-slate-700 px-7 py-3.5 text-sm font-semibold hover:border-slate-300 hover:bg-slate-50 transition-all duration-200"
            >
              See how it works
              <svg className="w-4 h-4" fill="none" viewBox="0 0 16 16" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M8 3v10M4 9l4 4 4-4" />
              </svg>
            </a>
          </div>
        </section>

        {/* ── How it works ────────────────────────────────────── */}
        <section id="how-it-works" className="bg-slate-50 border-y border-slate-100 py-16">
          <div className="max-w-4xl mx-auto px-6">
            <h2 className="text-2xl font-bold text-center text-slate-900 mb-10">
              How it works
            </h2>
            <ol className="grid sm:grid-cols-3 gap-6">
              {[
                {
                  step: "1",
                  title: "Answer guided questions",
                  body: "Step-by-step questions cover every section of Form N-400. Plain-English labels — no legal jargon.",
                },
                {
                  step: "2",
                  title: "AI explains every field",
                  body: "Tap the ? button next to any question to get an instant, plain-English explanation from our AI assistant.",
                },
                {
                  step: "3",
                  title: "Download your filled PDF",
                  body: "When you're done, we generate a pre-filled N-400 PDF. Review it, then print and file — or have an attorney review first.",
                },
              ].map(({ step, title, body }) => (
                <li key={step} className="bg-white rounded-2xl border border-slate-200/80 p-6 shadow-sm">
                  <div className="w-8 h-8 rounded-full bg-blue-600 text-white text-sm font-bold flex items-center justify-center mb-4">
                    {step}
                  </div>
                  <h3 className="font-semibold text-slate-900 mb-1.5">{title}</h3>
                  <p className="text-sm text-slate-500 leading-relaxed">{body}</p>
                </li>
              ))}
            </ol>
          </div>
        </section>

        {/* ── Pricing ─────────────────────────────────────────── */}
        <section className="max-w-3xl mx-auto px-6 py-16 text-center">
          <h2 className="text-2xl font-bold text-slate-900 mb-3">Simple pricing</h2>
          <p className="text-slate-500 text-sm mb-8 max-w-md mx-auto">
            Start for free. Pay only when you're ready to download.
          </p>

          <div className="inline-block bg-white border border-slate-200 rounded-2xl shadow-sm px-10 py-8 text-left">
            <div className="flex items-baseline gap-1 mb-1">
              <span className="text-4xl font-bold text-slate-900">$49</span>
              <span className="text-slate-400 text-sm">one-time</span>
            </div>
            <p className="text-sm text-slate-500 mb-5">Download your completed N-400 draft</p>

            <ul className="space-y-2 text-sm text-slate-700 mb-6">
              {[
                "Eligibility check included",
                "AI-assisted field explanations",
                "Document checklist",
                "Pre-filled PDF — ready to review",
                "Save and resume any time",
              ].map((item) => (
                <li key={item} className="flex items-center gap-2">
                  <span className="text-blue-500 shrink-0">✓</span>
                  {item}
                </li>
              ))}
            </ul>

            <Link
              to="/login"
              className="block text-center rounded-full bg-blue-600 text-white px-6 py-3 text-sm font-semibold hover:bg-blue-700 transition-colors shadow-sm"
            >
              Start for free →
            </Link>
          </div>

          <p className="mt-6 text-xs text-slate-400 max-w-sm mx-auto leading-relaxed">
            USCIS filing fees are separate and paid directly to USCIS.
            The amount depends on how you file and whether you qualify for a reduced fee.
          </p>
        </section>

        {/* ── Trust strip ─────────────────────────────────────── */}
        <section className="bg-slate-50 border-t border-slate-100 py-8">
          <div className="max-w-4xl mx-auto px-6">
            <ul className="flex flex-wrap items-center justify-center gap-x-8 gap-y-3 text-xs text-slate-500 font-medium">
              {[
                { icon: "🔒", text: "Encrypted in transit and at rest" },
                { icon: "👁", text: "You review before you file" },
                { icon: "🚫", text: "Never submitted automatically" },
                { icon: "📋", text: "N-400 edition: January 2025" },
              ].map(({ icon, text }) => (
                <li key={text} className="flex items-center gap-1.5">
                  <span>{icon}</span>
                  {text}
                </li>
              ))}
            </ul>
          </div>
        </section>
      </main>

      {/* ── Footer ──────────────────────────────────────────── */}
      <footer className="border-t border-slate-100 py-8">
        <div className="max-w-5xl mx-auto px-6">
          {/* Legal disclaimer */}
          <p className="text-xs text-slate-400 text-center leading-relaxed max-w-2xl mx-auto mb-6">
            GetCitizen AI is document preparation software, not a law firm and not an attorney.
            We do not provide legal advice or legal representation. If you have complex
            immigration circumstances, consult a licensed immigration attorney.
          </p>

          <div className="flex flex-col sm:flex-row items-center justify-between gap-3 text-xs text-slate-400">
            <Logo small />
            <nav className="flex items-center gap-5">
              <Link to="/terms" className="hover:text-slate-600 transition-colors">Terms</Link>
              <Link to="/privacy" className="hover:text-slate-600 transition-colors">Privacy</Link>
              <a href="mailto:support@getcitizen.ai" className="hover:text-slate-600 transition-colors">
                support@getcitizen.ai
              </a>
            </nav>
            <span>© {new Date().getFullYear()} GetCitizen AI</span>
          </div>
        </div>
      </footer>
    </div>
  );
}

// ── Shared logo mark ────────────────────────────────────────────────────────

function Logo({ small }: { small?: boolean }) {
  return (
    <div className="flex items-center gap-2">
      <div
        className={`rounded-xl bg-gradient-to-br from-blue-600 to-teal-500 flex items-center justify-center shadow-sm shrink-0 ${small ? "w-6 h-6" : "w-8 h-8"}`}
      >
        <svg
          className={`text-white ${small ? "w-3 h-3" : "w-4 h-4"}`}
          fill="none"
          viewBox="0 0 16 16"
          stroke="currentColor"
          strokeWidth="1.8"
        >
          <path strokeLinecap="round" strokeLinejoin="round" d="M8 1v14M1 8h14M4.5 4.5l7 7M11.5 4.5l-7 7" />
        </svg>
      </div>
      <span className={`font-bold tracking-tight text-slate-900 ${small ? "text-sm" : "text-base"}`}>
        GetCitizen <span className="text-blue-600">AI</span>
      </span>
    </div>
  );
}
